import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQueryHeader } from './DynamicBaseQueryHeader';

export const SurveyApi = createApi({
    reducerPath: 'SurveyApi',
    baseQuery: DynamicBaseQueryHeader('Survey'),
    tagTypes: ['SurveyCampaigns', 'SurveyDashboard'],
    endpoints: (builder) => ({
        surveyCheck: builder.query({
            query: (sourceId?: number) => ({
                url: `/check${sourceId ? `?sourceId=${sourceId}` : ''}`,
                method: "GET",
            }),
        }),
        surveyAction: builder.mutation({
            query: (body) => ({
                url: `/action`,
                method: "POST",
                body,
            }),
        }),
        surveySubmit: builder.mutation({
            query: (body) => ({
                url: `/submit`,
                method: "POST",
                body,
            }),
        }),
        surveyLater: builder.mutation({
            query: (body) => ({
                url: `/later`,
                method: "POST",
                body,
            }),
        }),
        surveyClose: builder.mutation({
            query: (body) => ({
                url: `/close`,
                method: "POST",
                body,
            }),
        }),
        getSurveyCampaigns: builder.query({
            query: () => ({
                url: `/admin/campaigns`,
                method: "GET",
            }),
            providesTags: ['SurveyCampaigns'],
        }),
        getSurveyCampaign: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}`,
                method: "GET",
            }),
            providesTags: ['SurveyCampaigns'],
        }),
        createSurveyCampaign: builder.mutation({
            query: (body) => ({
                url: `/admin/campaigns`,
                method: "POST",
                body,
            }),
            invalidatesTags: ['SurveyCampaigns'],
        }),
        updateSurveyCampaign: builder.mutation({
            query: (body) => ({
                url: `/admin/campaigns`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ['SurveyCampaigns'],
        }),
        activateSurveyCampaign: builder.mutation({
            query: (body) => ({
                url: `/admin/campaigns/activate`,
                method: "POST",
                body,
            }),
            invalidatesTags: ['SurveyCampaigns', 'SurveyDashboard'],
        }),
        deactivateSurveyCampaign: builder.mutation({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/deactivate`,
                method: "POST",
            }),
            invalidatesTags: ['SurveyCampaigns'],
        }),
        setSurveyExclusions: builder.mutation({
            query: (body) => ({
                url: `/admin/campaigns/exclusions`,
                method: "POST",
                body,
            }),
            invalidatesTags: ['SurveyCampaigns'],
        }),
        getSurveyExclusions: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/exclusions`,
                method: "GET",
            }),
        }),
        getSurveyEligibleUsers: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/eligible-users`,
                method: "GET",
            }),
        }),
        getSurveyExcludedUsers: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/excluded-users`,
                method: "GET",
            }),
        }),
        getSurveyCompletedUsers: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/completed-users`,
                method: "GET",
            }),
        }),
        getSurveyPopupNotCompletedUsers: builder.query({
            query: (surveyId: number) => ({
                url: `/admin/campaigns/${surveyId}/popup-not-completed-users`,
                method: "GET",
            }),
        }),
        getSurveyExclusionUsersLookup: builder.query({
            query: () => ({
                url: `/admin/campaigns/exclusion-users-lookup`,
                method: "GET",
            }),
        }),
        resetSurveyUsers: builder.mutation({
            query: (body) => ({
                url: `/admin/campaigns/reset-users`,
                method: "POST",
                body,
            }),
            invalidatesTags: ['SurveyDashboard'],
        }),
        getSurveyResponses: builder.mutation({
            query: (body) => ({
                url: `/admin/responses`,
                method: "POST",
                body,
            }),
        }),
        getSurveyActions: builder.mutation({
            query: (body) => ({
                url: `/admin/actions`,
                method: "POST",
                body,
            }),
        }),
        getSurveyAnalytics: builder.mutation({
            query: (body) => ({
                url: `/admin/analytics`,
                method: "POST",
                body,
            }),
        }),
        exportSurveyResponses: builder.mutation({
            query: (body) => ({
                url: `/admin/responses/export`,
                method: "POST",
                body,
                responseHandler: async (response: Response) => response.blob(),
            }),
        }),
        exportSurveyActions: builder.mutation({
            query: (body) => ({
                url: `/admin/actions/export`,
                method: "POST",
                body,
                responseHandler: async (response: Response) => response.blob(),
            }),
        }),
    }),
});

export const {
    useLazySurveyCheckQuery,
    useSurveyActionMutation,
    useSurveySubmitMutation,
    useSurveyLaterMutation,
    useSurveyCloseMutation,
    useGetSurveyCampaignsQuery,
    useCreateSurveyCampaignMutation,
    useUpdateSurveyCampaignMutation,
    useActivateSurveyCampaignMutation,
    useDeactivateSurveyCampaignMutation,
    useSetSurveyExclusionsMutation,
    useLazyGetSurveyExclusionsQuery,
    useLazyGetSurveyEligibleUsersQuery,
    useLazyGetSurveyExcludedUsersQuery,
    useLazyGetSurveyCompletedUsersQuery,
    useLazyGetSurveyPopupNotCompletedUsersQuery,
    useGetSurveyExclusionUsersLookupQuery,
    useResetSurveyUsersMutation,
    useGetSurveyResponsesMutation,
    useGetSurveyActionsMutation,
    useGetSurveyAnalyticsMutation,
    useExportSurveyResponsesMutation,
    useExportSurveyActionsMutation,
} = SurveyApi;
